/**
 * RevisionDrawer (T-043, PR-54, DES-15).
 *
 * Right-side drawer on /portal/tasks/:id. Section chips derived from the
 * deliverable structure + "What would you change?" (required) + "Why"
 * (optional) + a learn-note + submit. Submitting writes a revision_requests
 * row (RLS revision_requests_subscriber_insert checks is_owner; we supply
 * subscriber_id), then calls the `notify_revision_requested(:id)` RPC
 * (migration 0015, SECURITY DEFINER, granted to `authenticated`) to insert
 * the GUARANTEED operator notification. This replaces the prior best-effort
 * notifications insert that was always denied by RLS (frozen migration 0007
 * gives the subscriber no notifications INSERT). The RPC resolves the caller
 * via auth.uid() + is_owner() and is idempotent (one notification per
 * revision_request_id), so it is safe to call directly on the browser
 * session — no server endpoint and no operator-id lookup needed. The
 * authoritative operator surface remains the revision_requests row itself
 * (/eric/awaiting reads it directly), so a failed notify call never loses the
 * request.
 *
 * OQ-14 interpretation (revision cycle behavior — open question, sensible
 * default, documented in IMPL-NOTES, flagged for Eric/validator, NOT a
 * blocker): a revision stays on the SAME task and does NOT consume a fresh
 * active slot — Eric reframes the existing deliverable inside its original
 * cycle. After two revision rounds on the same deliverable, a third request
 * becomes a new task so the loop cannot run forever. The learn-note below
 * states this in Eric's client-communication register. If Eric resolves
 * OQ-14 differently, only this copy string + the operator-side accept/decline
 * handling (out of Phase G ownership) change.
 *
 * Submit is disabled until "What would you change?" has content (DES-15).
 * Drawer slides in from the right (DES-15 / ARCH-008 right-side Sheet alias).
 */

import { useState } from 'react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/toast';
import { createLogger } from '@studio/logger';

const revisionLogger = createLogger('portal');

const LEARN_NOTE =
  'How revisions work: this stays the same task. I reframe what I delivered without it counting as a new cycle, so your active slot is not used up. If a piece needs more than a couple of rounds, I will turn the next pass into its own task so we are not going in circles. You will see it move back into progress when I pick it up.';

/**
 * Section chips. Derived from the deliverable structure when known; falls back
 * to the generic DES-15 set (Skeleton / Voice / Sources / Other).
 */
function sectionChipsFor(sections: string[] | null): string[] {
  if (sections && sections.length > 0) return sections;
  return ['Skeleton', 'Voice', 'Sources', 'Other'];
}

export function RevisionDrawer({
  open,
  onOpenChange,
  taskId,
  subscriberId,
  deliverableSections,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  taskId: string;
  subscriberId: string;
  deliverableSections: string[] | null;
  onSubmitted: () => void;
}) {
  const chips = sectionChipsFor(deliverableSections);
  const [selected, setSelected] = useState<string[]>([]);
  const [whatToChange, setWhatToChange] = useState('');
  const [why, setWhy] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function toggleChip(c: string) {
    setSelected((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  }

  async function submit() {
    if (whatToChange.trim().length === 0) return;
    setSubmitting(true);
    try {
      const { data: inserted, error: revErr } = await supabase
        .from('revision_requests')
        .insert({
          task_id: taskId,
          subscriber_id: subscriberId,
          sections: selected,
          what_to_change: whatToChange.trim(),
          why: why.trim() || null,
        })
        .select('id')
        .single();
      if (revErr) throw revErr;

      // Guaranteed operator notification. notify_revision_requested (migration
      // 0015) is SECURITY DEFINER + granted to `authenticated`; it resolves
      // the caller via auth.uid() + is_owner(), validates ownership of this
      // revision request, and inserts exactly one 'revision_requested'
      // operator notification (idempotent per revision_request_id). It does
      // the privileged write the subscriber RLS forbids — no recipient lookup
      // here. The revision row is the authoritative operator surface
      // (/eric/awaiting reads it directly), so a notify error never loses the
      // request; we still confirm to the subscriber.
      const { error: notifyErr } = await supabase.rpc(
        'notify_revision_requested',
        { p_revision_request_id: inserted.id },
      );
      if (notifyErr) {
        // Non-fatal: the request itself landed and surfaces operator-side.
        revisionLogger.warn(
          { errMsg: notifyErr.message },
          'notify_revision_requested failed (revision still recorded)',
        );
      }

      toast.success('Sent. I will pick this back up.');
      setWhatToChange('');
      setWhy('');
      setSelected([]);
      onOpenChange(false);
      onSubmitted();
    } catch {
      toast.error('Could not send the revision request. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        side="right"
        className="w-full overflow-y-auto border-l border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] sm:max-w-md"
      >
        <DrawerHeader className="border-b border-[color:var(--color-line-dark)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--color-text-mute)]">
            Request a revision
          </p>
          <DrawerTitle className="font-display text-xl italic text-[color:var(--color-black)]">
            What needs adjusting?
          </DrawerTitle>
        </DrawerHeader>

        <div className="mt-4 space-y-5 px-1 pb-2">
          <div>
            <p className="mb-2 text-sm font-medium text-[color:var(--color-black)]">
              Which sections?
            </p>
            <div className="flex flex-wrap gap-1.5">
              {chips.map((c) => {
                const on = selected.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleChip(c)}
                    aria-pressed={on}
                    className={[
                      'rounded-md border-[1.5px] px-2.5 py-1.5 text-sm transition-colors',
                      on
                        ? 'border-[color:var(--color-navy)] bg-[color:var(--color-navy)] text-[color:var(--color-linen)]'
                        : 'border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] text-[color:var(--color-black)] hover:border-[color:var(--color-navy)]',
                    ].join(' ')}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label
              htmlFor="rev-what"
              className="mb-1.5 block text-sm font-medium text-[color:var(--color-black)]"
            >
              What would you change?
            </label>
            <textarea
              id="rev-what"
              value={whatToChange}
              onChange={(e) => setWhatToChange(e.target.value)}
              rows={5}
              className="w-full rounded-lg border-[1.5px] border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] p-3 text-sm leading-relaxed text-[color:var(--color-black)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
            />
          </div>

          <div>
            <label
              htmlFor="rev-why"
              className="mb-1.5 block text-sm font-medium text-[color:var(--color-black)]"
            >
              Why (optional)
            </label>
            <textarea
              id="rev-why"
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              rows={3}
              className="w-full rounded-lg border-[1.5px] border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] p-3 text-sm leading-relaxed text-[color:var(--color-black)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
            />
          </div>

          <div className="rounded-lg border border-[color:var(--color-rose)] bg-[color:var(--color-rose-subtle2)] px-4 py-3">
            <p className="text-[13px] leading-relaxed text-[color:var(--color-black)]">
              {LEARN_NOTE}
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <DrawerClose
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm text-[color:var(--color-text-mute)] transition-colors hover:text-[color:var(--color-navy)]"
            >
              Cancel
            </DrawerClose>
            <button
              type="button"
              onClick={submit}
              disabled={whatToChange.trim().length === 0 || submitting}
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[color:var(--color-rose)] px-5 py-2 text-sm font-medium text-[color:var(--color-navy)] transition-colors hover:bg-[color:var(--color-rose-dark)] disabled:opacity-45"
            >
              {submitting ? 'Sending...' : 'Send revision request →'}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
